import React, { useState, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Document, Page } from 'react-pdf';
import styled from "styled-components";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import Zoom from "../../assets/images/Zoom.svg";
import * as XLSX from "xlsx";
import { renderAsync } from "docx-preview";
import { DOC_FILE_TYPES, PDF_FILE_TYPES, XLSX_FILE_TYPES } from '../../utils/constants';

const CloseButton = styled.button`
  right: 24px;
  top: 20px;
  background: none;
  border: none;
  font-size: 20px;
  color: #AEB8C4;
  cursor: pointer;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7) !important;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1500;
  overflow: auto;
  scrollbar-width: none;
`;

const ModalContainer = styled.div`
  // background: #555556;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
`;

const ModalHeader = styled.div`
      display: flex;
      font-family: Manrope !important;
    justify-content: space-between;
    align-items: center;
    padding: 10px 24px;
    background-color: #4e4e4f;
    color: white;
    height: 64px;
  span {
    font-size: 20px;
  }

  button {
    background: #transparent;
    border: none;
    font-size: 1.2rem;
    cursor: pointer;
  }
`;

const MainPdfViewer = styled.div`
  flex: 1;
  position: relative;
  padding: 16px;
  margin: 0 auto;
  width: 100%;
  max-width: 900px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;

  /* Scrollbar styles for Webkit (Chrome, Safari, Edge) */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(100, 100, 100, 0.5); /* semi-transparent gray */
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(100, 100, 100, 0.8);
  }

  /* Scrollbar styles for Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 100, 100, 0.5) transparent;

  .my-docx-preview-wrapper {
    background-color: transparent !important;
  }
  
  .my-docx-preview .docx-wrapper section.docx {
    border: none !important;
    box-shadow: none !important;
  }
  
  .my-docx-preview section.docx {
    border: none !important;
    box-shadow: none !important;
  }
  
  .docx-wrapper {
    border: none !important;
  }
`;

const OverlayToolbar = styled.div`
  position: fixed;
  left: 50%;
  bottom: 48px; /* slightly above the modal footer */
  transform: translateX(-50%);
  background-color: #2c2c2c;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 99999;
  box-shadow: 0 2px 8px rgba(0,0,0,0.25);
`;

const ThumbnailStrip = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;   /* ✅ center thumbnails */
  align-items: flex-start;
  gap: 30px;                 /* ✅ space between each thumbnail */
  padding: 8px 12px;
  background-color: #f0f0f0;
  overflow-x: auto;
  overflow-y: hidden;
  border-top: 1px solid #ccc;
  max-height: 140px;
`;

const ThumbWrapper = styled.div`
  display: inline-block;
  flex: 0 0 auto;
  border: 2px solid transparent;
  border-radius: 4px;
  cursor: pointer;
  transition: border 0.3s;
  background: white;

  &:hover {
    border: 2px solid #0C53BB;
  }

  canvas {
    display: block;
    width: 90px !important;
    height: auto !important;
  }
`;

const ModalFooter = styled.div`
  text-align: right;
  font-size: 13px;
  padding: 10px 12px;
  background-color: #ffffff;
  button {
    background-color: #f3f4f7;
    color: #1b2028;
    border: none;
    padding: 6px 12px;
    border-radius: 6px;
    cursor: pointer;
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #2c2c2c;
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  position: sticky;
    bottom: 0%;
    width: fit-content;
    margin: 0 auto;
    z-index: 99999999;
`;

const Section = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Divider = styled.div`
  width: 1px;
  height: 24px;
  background-color: #555;
  margin: 0 12px;
`;

const PageInput = styled.input`
  width: 32px;
  text-align: center;
  background-color: #3a3a3a;
  border: none;
  color: white;
  border-radius: 4px;
  padding: 2px;
`;

const IconButton = styled.button`
  background-color: transparent;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;

  &:hover {
    color: #ccc;
  }
`;

const HideScrollbar = styled.div`
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Webkit */
  }
`;

const PrintPreview = ({ printData, setOpenPreview, type }) => {
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [excelData, setExcelData] = useState([]);
  const containerRef = useRef(null);
  const [selectedPage, setSelectedPage] = useState(1);
  const [docxScale, setDocxScale] = useState(1);
  const [xlsxScale, setXlsxScale] = useState(1);
  const [pdfError, setPdfError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [zoomWarning, setZoomWarning] = useState(false);
  const [docxLoaded, setDocxLoaded] = useState(false);
  
  // Refs for scrollable containers
  const docxScrollRef = useRef(null);
  const xlsxScrollRef = useRef(null);
  
  // Debounce timer refs
  const zoomTimeoutRef = useRef(null);

  // Define base64ToUint8Array function before using it in useMemo
  const base64ToUint8Array = useCallback((base64) => {
    try {
      if (!base64 || typeof base64 !== 'string') {
        throw new Error('Invalid base64 string');
      }
      const raw = atob(base64);
      const uint8Array = new Uint8Array(raw.length);
      for (let i = 0; i < raw.length; ++i) {
        uint8Array[i] = raw.codePointAt(i);
      }
      return uint8Array;
    } catch (error) {
      console.error('Error converting base64 to Uint8Array:', error);
      return null;
    }
  }, []);

  // Create a stable PDF file object that doesn't change unnecessarily
  const pdfFile = useMemo(() => {
    if (type === "sign" || !PDF_FILE_TYPES.includes(printData.file_type) || !printData.file_content) return null;
    
    // Create a hash of the content to ensure stability
    const contentHash = printData.file_content.substring(0, 100);
    
    try {
      const fileData = base64ToUint8Array(printData.file_content);
      if (fileData) {
        // Create a completely fresh copy to avoid ArrayBuffer detachment
        const freshData = new Uint8Array(fileData);
        return { 
          data: freshData,
          // Add a stable identifier to prevent unnecessary re-renders
          _id: contentHash,
          _type: 'pdf'
        };
      }
      return null;
    } catch (error) {
      console.error('Error creating PDF file:', error);
      return null;
    }
  }, [printData.file_content, printData.file_type, type]);

  // Debounced zoom functions to prevent rapid state changes
  const debouncedZoom = useCallback((zoomFunction, delay = 100) => {
    if (zoomTimeoutRef.current) {
      clearTimeout(zoomTimeoutRef.current);
    }
    zoomTimeoutRef.current = setTimeout(() => {
      zoomFunction();
    }, delay);
  }, []);

  // Helper function to handle zoom scale updates
  const updateZoomScale = useCallback((setter, operation) => {
    debouncedZoom(() => setter(prev => operation(prev)), 100);
  }, [debouncedZoom]);

  // Helper function to handle zoom warning
  const handleZoomWarning = (newScale) => {
    if (newScale >= 2) {
      setZoomWarning(true);
      setTimeout(() => setZoomWarning(false), 3000);
    }
  };

  const zoomIn = useCallback(() => {
    debouncedZoom(() => {
      setScale(prev => {
        const newScale = Math.min(prev + 0.2, 2.5);
        handleZoomWarning(newScale);
        return newScale;
      });
    }, 100);
  }, [debouncedZoom]);

  const zoomOut = useCallback(() => {
    updateZoomScale(setScale, prev => Math.max(prev - 0.2, 0.5));
  }, [updateZoomScale]);

  const zoomInDocx = useCallback(() => {
    updateZoomScale(setDocxScale, prev => Math.min(prev + 0.2, 2.5));
  }, [updateZoomScale]);

  const zoomOutDocx = useCallback(() => {
    updateZoomScale(setDocxScale, prev => Math.max(prev - 0.2, 0.5));
  }, [updateZoomScale]);

  const zoomInXlsx = useCallback(() => {
    updateZoomScale(setXlsxScale, prev => Math.min(prev + 0.2, 2.5));
  }, [updateZoomScale]);

  const zoomOutXlsx = useCallback(() => {
    updateZoomScale(setXlsxScale, prev => Math.max(prev - 0.2, 0.5));
  }, [updateZoomScale]);

  const resetZoom = useCallback(() => {
    setScale(1.0);
    setDocxScale(1);
    setXlsxScale(1);
  }, []);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current);
      }
    };
  }, []);

  const printClose = () => {
    setOpenPreview(false);
  };

  const updateSelectedPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= numPages) {
      setSelectedPage(pageNumber);
    }
  };

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setPdfError(null);
    setIsLoading(false);
    setRenderedThumbnails(new Set());
    // Ensure selected page is within bounds
    if (selectedPage > numPages) {
      setSelectedPage(1);
    }
  }, [selectedPage]);

  const onDocumentLoadStart = useCallback(() => {
    setIsLoading(true);
    setPdfError(null);
  }, []);

  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    
    // Check if it's an ArrayBuffer detachment error
    if (error?.message?.includes('ArrayBuffer')) {
      console.warn('ArrayBuffer detachment detected, this is likely a thumbnail issue');
      // Don't set error for ArrayBuffer issues, just log them
      return;
    }
    
    // Check if it's a worker termination error
    if (error?.message?.includes('Worker task was terminated')) {
      console.warn('Worker task terminated, this is likely a temporary issue');
      // Don't set error for worker termination, just log them
      return;
    }
    
    // Check if it's a worker destruction error
    if (error?.message?.includes('Worker was destroyed')) {
      console.warn('Worker was destroyed, this is likely a temporary issue');
      // Don't set error for worker destruction, just log them
      return;
    }
    
    setPdfError('Failed to load PDF. Please try again.');
    setIsLoading(false);
    
    // Don't reset the worker - let PDF.js handle it
  }, []);

  const retryPdfLoad = useCallback(() => {
    setPdfError(null);
    setIsLoading(true);
    setRenderedThumbnails(new Set());
    
    // Don't reset the worker - let PDF.js handle it naturally
    // This prevents unnecessary worker destruction
    
    // Force a re-render by updating a dependency
    setSelectedPage(prev => prev);
    
    // Add a small delay to ensure state updates take effect
    setTimeout(() => {
      setIsLoading(false);
    }, 100);
  }, []);

  const onPageLoadError = useCallback((error) => {
    console.error('Page load error:', error);
    
    // Check if it's an ArrayBuffer detachment error
    if (error?.message?.includes('ArrayBuffer')) {
      console.warn('ArrayBuffer detachment detected, this is likely a thumbnail issue');
      // Don't set error for ArrayBuffer issues, just log them
      return;
    }
    
    // Check if it's a worker termination error
    if (error?.message?.includes('Worker task was terminated')) {
      console.warn('Worker task terminated, this is likely a temporary issue');
      // Don't set error for worker termination, just log them
      return;
    }
    
    // Check if it's a worker destruction error
    if (error?.message?.includes('Worker was destroyed')) {
      console.warn('Worker was destroyed, this is likely a temporary issue');
      // Don't set error for worker destruction, just log them
      return;
    }
    
    // Check if it's a sendWithPromise error
    if (error?.message?.includes('sendWithPromise')) {
      console.warn('Worker communication error, this is likely a temporary issue');
      // Don't set error for worker communication issues, just log them
      return;
    }
    
    setPdfError('Failed to load page. Please try again.');
    setIsLoading(false);
    
    // Don't reset the worker - let PDF.js handle it
  }, []);

  // Helper: Parse XLSX data
  function parseXlsx(arrayBuffer) {
    try {
      if (!arrayBuffer) {
        console.error('No array buffer provided for XLSX parsing');
        return;
      }
      const workbook = XLSX.read(arrayBuffer, { type: "array" });
      if (!workbook?.SheetNames?.length) {
        console.error('Invalid XLSX workbook');
        return;
      }
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      if (!worksheet?.['!ref']) {
        console.error('Invalid XLSX worksheet');
        return;
      }
      const range = XLSX.utils.decode_range(worksheet['!ref']);
      const rows = [];
      for (let rowNum = range.s.r; rowNum <= range.e.r; rowNum++) {
        const row = [];
        for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
          const cellAddress = XLSX.utils.encode_cell({ r: rowNum, c: colNum });
          const cell = worksheet[cellAddress];
          row.push(cell ? cell.v : "");
        }
        rows.push(row);
      }
      setExcelData(rows);
    } catch (error) {
      console.error('Error parsing XLSX:', error);
      setExcelData([]);
    }
  }

  // Helper: Render DOCX data
  async function renderDocx(arrayBuffer) {
    try {
      if (!arrayBuffer) {
        console.error('No array buffer provided for DOCX rendering');
        setDocxLoaded(false);
        return;
      }
      if (!containerRef.current) {
        console.error('Container ref not available for DOCX rendering');
        setDocxLoaded(false);
        return;
      }
      
      // Clear previous content
      containerRef.current.innerHTML = '';
      setDocxLoaded(false);
      
      await renderAsync(arrayBuffer, containerRef.current, undefined, {
        className: "my-docx-preview",
        inWrapper: true,
        breakPages: true,
        useBase64URL: true,
      });
      
      console.log('DOCX rendered successfully');
      setDocxLoaded(true);
    } catch (error) {
      console.error('Error rendering DOCX:', error);
      setDocxLoaded(false);
    }
  }

  // After zooming, scroll to left edge for DOCX and XLSX
  React.useEffect(() => {
    if (DOC_FILE_TYPES.includes(printData.file_type) && docxScrollRef.current) {
      docxScrollRef.current.scrollLeft = 0;
    }
  }, [docxScale, printData]);
  
  React.useEffect(() => {
    if (XLSX_FILE_TYPES.includes(printData.file_type) && xlsxScrollRef.current) {
      xlsxScrollRef.current.scrollLeft = 0;
    }
  }, [xlsxScale, printData]);

  React.useEffect(() => {
    const loadData = async () => {
      if (type !== 'sign' && printData.file_content) {
        if (XLSX_FILE_TYPES.includes(printData.file_type)) {
          const arrayBuffer = base64ToUint8Array(printData.file_content);
          if (!arrayBuffer) {
            console.error('Failed to convert file content');
            return;
          }
          parseXlsx(arrayBuffer);
        } else if (DOC_FILE_TYPES.includes(printData.file_type)) {
          const arrayBuffer = base64ToUint8Array(printData.file_content);
          if (!arrayBuffer) {
            console.error('Failed to convert file content');
            return;
          }
          setDocxLoaded(false); // Reset state before loading
          await renderDocx(arrayBuffer);
        }
      }
    };
    loadData();
    return () => {};
  }, [printData, type, base64ToUint8Array]);



  // Helper: Render PDF content
  function renderPdfContent() {
    if (!pdfFile) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          color: '#ff6b6b',
          fontSize: '16px'
        }}>
          No PDF file available
        </div>
      );
    }

    if (pdfError) {
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          color: '#ff6b6b',
          fontSize: '16px',
          gap: '16px'
        }}>
          <div>{pdfError}</div>
          <button 
            onClick={retryPdfLoad}
            style={{
              background: '#0C53BB',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Retry
          </button>
        </div>
      );
    }

    if (!selectedPage || selectedPage < 1) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          color: '#ff6b6b',
          fontSize: '16px'
        }}>
          Invalid page number
        </div>
      );
    }

        // Add error boundary for PDF rendering
    try {
      const mainPdfFile = createMainPdfFile();
      
      if (!mainPdfFile) {
        return (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '100%',
            color: '#ff6b6b',
            fontSize: '16px'
          }}>
            Failed to create PDF file
          </div>
        );
      }
      
      return (
        <Document
          file={mainPdfFile}
          loading={
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%' 
            }}>
              Loading PDF...
            </div>
          }
          onLoadError={onDocumentLoadError}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadStart={onDocumentLoadStart}
          onRenderError={onPageLoadError}
          error={
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: '100%',
              color: '#ff6b6b'
            }}>
              Error loading PDF
            </div>
          }
        >
          {Boolean(selectedPage) && (
            <Page
              pageNumber={selectedPage}
              width={800}
              scale={scale}
              onLoadError={onPageLoadError}
              loading={
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%' 
                }}>
                  Loading page...
                </div>
              }
            />
          )}
        </Document>
      );
    } catch (error) {
      console.error('Error rendering PDF:', error);
      return (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          color: '#ff6b6b',
          fontSize: '16px',
          gap: '16px'
        }}>
          <div>Error rendering PDF</div>
          <button 
            onClick={retryPdfLoad}
            style={{
              background: '#0C53BB',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Retry
          </button>
        </div>
      );
    }
  }

  // Helper: Render XLSX content
  function renderXlsxContent() {
    if (!excelData || excelData.length === 0) {
      return (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          color: '#ff6b6b',
          fontSize: '16px'
        }}>
          Loading XLSX content...
        </div>
      );
    }
    
    const baseWidth = 1200;
    return (
      <div
        ref={xlsxScrollRef}
        style={{
          background: 'white',
          marginTop: 20,
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '70vh',
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <div
          style={{
            width: `${baseWidth}px`,
            transform: `scale(${xlsxScale})`,
            transformOrigin: 'top left',
            transition: 'transform 0.2s',
            display: 'inline-block',
          }}
        >
          {excelData.length > 0 && excelData[0] && (
            <table
              style={{
                borderCollapse: 'collapse',
                minWidth: excelData[0].length ? `${excelData[0].length * 120}px` : '1200px',
                tableLayout: 'auto',
              }}
            >
              <tbody>
                {excelData.map((row, rowIndex) => (
                  <tr key={rowIndex + '-' + (row?.[0] || 'empty')}>
                    {row?.map((cell, cellIndex) => (
                      <td
                        key={cellIndex + '-' + rowIndex}
                        style={{
                          border: '1px solid #ccc',
                          padding: '6px 12px',
                          minWidth: 80,
                          background: rowIndex === 0 ? '#f3f3f3' : 'white',
                          fontWeight: rowIndex === 0 ? 'bold' : 'normal',
                          textAlign: 'left',
                        }}
                      >
                        {cell ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  // Helper: Render DOCX content
  function renderDocxContent() {
    const baseWidth = 800;
    return (
      <div
        ref={docxScrollRef}
        style={{
          marginTop: 20,
          overflowX: 'auto',
          overflowY: 'auto',
          maxHeight: '70vh',
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          position: 'relative',
        }}
      >
        {!docxLoaded && (
          <div style={{
            position: 'absolute',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            color: '#666',
            fontSize: '16px',
            zIndex: 10,
          }}>
            Loading DOCX content...
          </div>
        )}
        <div
          style={{
            width: `${baseWidth}px`,
            transform: `scale(${docxScale})`,
            transformOrigin: 'top left',
            transition: 'transform 0.2s',
            display: 'inline-block',
            opacity: docxLoaded ? 1 : 0,
          }}
        >
          <div ref={containerRef}></div>
        </div>
      </div>
    );
  }

  // Helper: Render main content based on file type
  function renderMainContent() {
    if (type === "sign") return null;
    if (!printData?.file_type || !printData?.file_content) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          color: '#ff6b6b',
          fontSize: '16px'
        }}>
          No file data available
        </div>
      );
    }
    
    // Validate file type
    const validFileTypes = ['pdf', 'xlsx', 'docx'];
    if (!validFileTypes.includes(printData.file_type)) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          color: '#ff6b6b',
          fontSize: '16px'
        }}>
          Unsupported file type: {printData.file_type}
        </div>
      );
    }
    
    if (PDF_FILE_TYPES.includes(printData.file_type)) return renderPdfContent();
    if (XLSX_FILE_TYPES.includes(printData.file_type)) return renderXlsxContent();
    if (DOC_FILE_TYPES.includes(printData.file_type)) {
      return (
        <div style={{ position: 'relative', height: '100%' }}>
          <HideScrollbar style={{ height: '100%', overflowY: 'auto' }}>
            {renderDocxContent()}
          </HideScrollbar>
          <OverlayToolbar>
            <Section>
              <IconButton onClick={zoomOutDocx}>−</IconButton>
              <img src={Zoom} alt="Zoom Icon" style={{ width: '20px', height: '20px' }} />
              <IconButton onClick={zoomInDocx}>+</IconButton>
            </Section>
          </OverlayToolbar>
        </div>
      );
    }
    return null;
  }

  // Create a single stable PDF file reference that can be shared
  const stablePdfFileRef = useRef(null);
  
  // Helper: Create a fresh copy of PDF data to avoid ArrayBuffer detachment issues
  const createThumbnailPdfFile = useCallback(() => {
    if (!printData.file_content) return null;
    
    // Check if we already have a stable reference
    if (stablePdfFileRef.current && 
        stablePdfFileRef.current._contentHash === printData.file_content.substring(0, 100)) {
      return stablePdfFileRef.current;
    }
    
    try {
      const fileData = base64ToUint8Array(printData.file_content);
      if (fileData) {
        // Create a completely fresh copy to avoid ArrayBuffer detachment
        const freshData = new Uint8Array(fileData);
        const newPdfFile = { 
          data: freshData,
          _contentHash: printData.file_content.substring(0, 100),
          _timestamp: Date.now()
        };
        stablePdfFileRef.current = newPdfFile;
        return newPdfFile;
      }
      return null;
    } catch (error) {
      console.error('Error creating thumbnail PDF file:', error);
      return null;
    }
  }, [printData.file_content]);

  // Helper: Create a fresh copy of PDF data for main PDF to avoid ArrayBuffer detachment issues
  const createMainPdfFile = useCallback(() => {
    if (!printData.file_content) return null;
    
    // Use the same stable reference as thumbnails to prevent worker conflicts
    return createThumbnailPdfFile();
  }, [createThumbnailPdfFile]);

  const [renderedThumbnails, setRenderedThumbnails] = useState(new Set());
  
  // Gradually render more thumbnails to prevent worker overload
  React.useEffect(() => {
    if (numPages > 0 && renderedThumbnails.size < Math.min(numPages, 5)) {
      const timer = setTimeout(() => {
        setRenderedThumbnails(prev => {
          const newSet = new Set(prev);
          // Add next few thumbnails
          for (let i = renderedThumbnails.size; i < Math.min(renderedThumbnails.size + 2, numPages); i++) {
            newSet.add(i + 1);
          }
          return newSet;
        });
      }, 500); // 500ms delay between batches
      
      return () => clearTimeout(timer);
    }
  }, [numPages, renderedThumbnails.size]);

    // Helper: Render PDF thumbnails
    function renderThumbnails() {
      if (type !== "sign" && printData && (PDF_FILE_TYPES.includes(printData.file_type)) && numPages > 0 && pdfFile) {
        return (
          <ThumbnailStrip>
            <Document file={pdfFile} loading="Loading thumbnails...">
              {Array.from({ length: numPages }, (_, i) => (
                <ThumbWrapper
                  key={`thumb-${i+1}`}
                  onClick={() => updateSelectedPage(i + 1)}
                  style={{ border: selectedPage === i+1 ? '2px solid #0C53BB' : undefined }}
                >
                  <Page pageNumber={i + 1} width={60} />
                </ThumbWrapper>
              ))}
            </Document>
          </ThumbnailStrip>
        );
      }
      return null;
    }

  return (
    <ModalOverlay>
      <ModalContainer>
        <ModalHeader>
          <span>Preview Template</span>
          <CloseButton onClick={printClose} aria-label="Close">
            &#10005;
          </CloseButton>
        </ModalHeader>
        <MainPdfViewer>
          {isLoading && (
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1000,
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '20px',
              borderRadius: '8px',
              fontSize: '16px'
            }}>
              Loading document...
            </div>
          )}
          {zoomWarning && (
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              background: '#ff9800',
              color: 'white',
              padding: '12px 20px',
              borderRadius: '6px',
              fontSize: '14px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }}>
              High zoom level may affect performance
            </div>
          )}
          {renderMainContent()}
          {(printData && (PDF_FILE_TYPES.includes(printData.file_type)) && numPages > 0) && (
            <OverlayToolbar>
              {numPages > 1 && (
                <Section>
                  <span>Page</span>
                  <PageInput
                    type="number"
                    value={selectedPage}
                    min={1}
                    max={numPages}
                    onChange={event => {
                      const val = Number(event.target.value);
                      if (val >= 1 && val <= numPages) setSelectedPage(val);
                    }}
                  />
                  <span>/ {numPages}</span>
                </Section>
              )}
              {numPages > 1 && <Divider />}
              <Section>
                <IconButton onClick={zoomOut}>−</IconButton>
                <img src={Zoom} alt="Zoom Icon" style={{ width: '20px', height: '20px' }} />
                <IconButton onClick={zoomIn}>+</IconButton>
              </Section>
              <Divider />
              <Section>
                <IconButton 
                  onClick={resetZoom}
                  style={{ fontSize: '12px', padding: '4px 8px' }}
                  title="Reset Zoom"
                >
                  Reset
                </IconButton>
              </Section>
            </OverlayToolbar>
          )}
          {(printData && DOC_FILE_TYPES.includes(printData.file_type)) && (
            <OverlayToolbar>
              <Section>
                <IconButton onClick={zoomOutDocx}>−</IconButton>
                <img src={Zoom} alt="Zoom Icon" style={{ width: '20px', height: '20px' }} />
                <IconButton onClick={zoomInDocx}>+</IconButton>
              </Section>
            </OverlayToolbar>
          )}
          {(printData && XLSX_FILE_TYPES.includes(printData.file_type)) && (
            <OverlayToolbar>
              <Section>
                <IconButton onClick={zoomOutXlsx}>−</IconButton>
                <img src={Zoom} alt="Zoom Icon" style={{ width: '20px', height: '20px' }} />
                <IconButton onClick={zoomInXlsx}>+</IconButton>
              </Section>
            </OverlayToolbar>
          )}
        </MainPdfViewer>
        {numPages > 0 && renderThumbnails()}
        <ModalFooter>
          <button onClick={printClose}>Close Preview</button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

PrintPreview.propTypes = {
  printData: PropTypes.shape({
    file_content: PropTypes.string.isRequired,
    file_type: PropTypes.string.isRequired,
  }).isRequired,
  setOpenPreview: PropTypes.func.isRequired,
  type: PropTypes.string,
};

export default PrintPreview;